import React, { useState, useCallback, useEffect } from 'react';
import Popup from '@/shared/popups/popupDetails';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { geocodeByLatLng } from 'react-google-places-autocomplete';
import { useTranslation } from 'react-i18next';
export interface LocationData {
  lat: number;
  lng: number;
  address?: string;
}

interface LocationPickerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (location: LocationData) => void;
  initialLocation?: LocationData;
  googlePlacesApiKey: string;
}

const defaultCenter = { lat: 18.2208, lng: -66.5901 }; // Puerto Rico center

const LocationPickerPopup: React.FC<LocationPickerPopupProps> = ({ isOpen, onClose, onSubmit, initialLocation, googlePlacesApiKey }) => {
  const [marker, setMarker] = useState<{ lat: number; lng: number }>(initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : defaultCenter);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(marker);
  const [address, setAddress] = useState<string>(initialLocation?.address || '');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Recenter map and marker when initialLocation changes
  useEffect(() => {
    if (initialLocation && initialLocation.lat && initialLocation.lng) {
      setMarker({ lat: initialLocation.lat, lng: initialLocation.lng });
      setMapCenter({ lat: initialLocation.lat, lng: initialLocation.lng });
      setAddress(initialLocation.address || '');
    }
  }, [initialLocation]);

  // When marker is dragged
  const handleMarkerDrag = useCallback(async (e: any) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    setLoading(true);
    try {
      const results = await geocodeByLatLng({ lat, lng });
      if (results && results[0]) {
        setAddress(results[0].formatted_address);
      } else {
        setAddress('');
      }
    } catch (err) {
      setAddress('');
    } finally {
      setLoading(false);
    }
  }, []);

  // On submit
  const handleSubmit = () => {
    if (!marker.lat || !marker.lng) return;
    onSubmit({ lat: marker.lat, lng: marker.lng, address });
  };

  return (
    <Popup
      isOpen={isOpen}
      handleClose={onClose}
      className="bg-white"
      size="lg"
      headerTitle={t('selectLocation')}
      isShowCloseIcon
      isShowBlackCrossIcon
      slotProps={{
        root: { sx: { zIndex: 4001 } }
      }}
      bodyChildren={
        <div className="flex flex-col gap-4 w-full h-[500px]">
          <APIProvider apiKey={googlePlacesApiKey} libraries={['places']}>
            <div className="flex-1 rounded-lg overflow-hidden min-h-[300px]">
              <Map
                center={mapCenter}
                defaultCenter={mapCenter}
                defaultZoom={14}
                style={{ width: '100%', height: '100%' }}
                gestureHandling="greedy"
                zoomControl={true}
                restriction={{
                  latLngBounds: {
                    north: 18.52,
                    south: 17.85,
                    west: -67.3,
                    east: -65.2,
                  },
                  strictBounds: true,
                }}
              >
                <Marker
                  position={marker}
                  draggable={true}
                  onDragEnd={handleMarkerDrag}
                />
              </Map>
            </div>
          </APIProvider>
          <div className="flex sm:flex-row flex-col gap-2 justify-between items-center mt-2">
            <span className="text-gray-600 text-sm truncate max-w-[70%]">
              {loading ? 'Cargando dirección...' : (address || t('dragLocation'))}
            </span>
            <button
              className="bg-blue-600 text-white px-6 py-2 rounded-3xl font-semibold"
              onClick={handleSubmit}
              disabled={!marker.lat || !marker.lng}
            >
              {t('saveLocation')}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default LocationPickerPopup; 